import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { useBusinessUnits } from '../../hooks/useBusinessUnits';
import { Invoice, BusinessUnit } from '../../types/invoice';
import api from '../../config/axios';

interface CompanyAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onAssignmentComplete: () => void;
}

const CompanyAssignmentDialog: React.FC<CompanyAssignmentDialogProps> = ({
  open,
  onClose,
  invoice,
  onAssignmentComplete
}) => {
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState<string>('');
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { businessUnits, loading: businessUnitsLoading, error: businessUnitsError } = useBusinessUnits();



  useEffect(() => {
    if (invoice && invoice.businessUnitId) {
      setSelectedBusinessUnitId(invoice.businessUnitId.toString());
    } else {
      setSelectedBusinessUnitId('');
    }
  }, [invoice]);

  const handleAssign = async () => {
    if (!invoice || !selectedBusinessUnitId) return;

    setAssigning(true);
    setError(null);

    try {
      await api.patch(`/invoices/${invoice.id}/assign-business-unit`, {
        businessUnitId: parseInt(selectedBusinessUnitId)
      });

      onAssignmentComplete();
      onClose();
    } catch (err: any) {
      console.error('Error assigning business unit:', err);
      setError(err.response?.data?.message || 'Chyba při přiřazování firmy');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async () => {
    if (!invoice) return;

    setAssigning(true);
    setError(null);

    try {
      await api.patch(`/invoices/${invoice.id}/assign-business-unit`, {
        businessUnitId: null
      });

      onAssignmentComplete();
      onClose();
    } catch (err: any) {
      console.error('Error unassigning business unit:', err);
      setError(err.response?.data?.message || 'Chyba při odstraňování přiřazení firmy');
    } finally {
      setAssigning(false);
    }
  };

  const selectedBusinessUnit = businessUnits.find(bu => bu.id.toString() === selectedBusinessUnitId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Přiřadit firmu k faktuře
      </DialogTitle>
      <DialogContent>
        {invoice && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Faktura: {invoice.invoiceNumber}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Dodavatel: {invoice.supplierName}
            </Typography>
            {invoice.supplierICO && (
              <Typography variant="body2" color="textSecondary">
                IČO: {invoice.supplierICO}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Vybrat firmu</InputLabel>
          <Select
            value={selectedBusinessUnitId}
            onChange={(e) => setSelectedBusinessUnitId(e.target.value)}
            label="Vybrat firmu"
            disabled={assigning || businessUnitsLoading}
          >
            <MenuItem value="">
                              <em>Žádná účetní jednotka (odstranit přiřazení)</em>
            </MenuItem>
            {businessUnits.length === 0 ? (
              <MenuItem disabled>
                <em>Žádné firmy k dispozici</em>
              </MenuItem>
            ) : (
              businessUnits.map((bu) => (
                <MenuItem key={bu.id} value={bu.id.toString()}>
                  <Box>
                    <Typography variant="body2">{bu.name}</Typography>
                    {bu.ico && (
                      <Typography variant="caption" color="textSecondary">
                        IČO: {bu.ico}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {selectedBusinessUnit && (
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
                              Vybraná účetní jednotka:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {selectedBusinessUnit.name}
            </Typography>
            {selectedBusinessUnit.ico && (
              <Typography variant="body2" color="textSecondary">
                IČO: {selectedBusinessUnit.ico}
              </Typography>
            )}
            {selectedBusinessUnit.dic && (
              <Typography variant="body2" color="textSecondary">
                DIČ: {selectedBusinessUnit.dic}
              </Typography>
            )}
          </Box>
        )}

        {businessUnitsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {businessUnitsError && (
          <Alert severity="error">
            Chyba při načítání firem: {businessUnitsError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>
          Zrušit
        </Button>
        {selectedBusinessUnitId && (
          <Button
            onClick={handleAssign}
            variant="contained"
            disabled={assigning}
            startIcon={assigning ? <CircularProgress size={16} /> : undefined}
          >
            {assigning ? 'Přiřazování...' : 'Přiřadit'}
          </Button>
        )}
        {!selectedBusinessUnitId && invoice?.businessUnitId && (
          <Button
            onClick={handleUnassign}
            variant="outlined"
            color="warning"
            disabled={assigning}
            startIcon={assigning ? <CircularProgress size={16} /> : undefined}
          >
            {assigning ? 'Ostraňování...' : 'Odstranit přiřazení'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CompanyAssignmentDialog;
